// Bar POS: touch grid by category → cart → cash / card / charge to room.
import React, { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, Trash2, Banknote, CreditCard, BedDouble, Loader2, CheckCircle2, Printer } from 'lucide-react';
import { printReceipt } from './receipt';
import { apiCall } from '../_shared';
import { CAT_LABEL, money } from './BarTab';

const PayButton = ({ id, icon: Icon, label, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`bar-pay-${id}`}
    className={`flex-1 inline-flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs font-semibold transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50 text-slate-700'}`}>
    <Icon className="w-4 h-4" /> {label}
  </button>
);

const BarPos = ({ products, onSold, labels = CAT_LABEL }) => {
  const cats = useMemo(() => [...new Set(products.map((p) => p.category))], [products]);
  const [cat, setCat] = useState('all');
  const [cart, setCart] = useState({});
  const [payment, setPayment] = useState('cash');
  const [spot, setSpot] = useState('');
  const [spots, setSpots] = useState([]);
  useEffect(() => { apiCall('/api/admin/terrace/spots').then((r) => setSpots(r.spots.filter((x) => x.active))).catch(() => {}); }, []);
  const [folios, setFolios] = useState([]);
  const [bookingId, setBookingId] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (payment !== 'room') return;
    apiCall('/api/admin/bar/in-house').then((r) => setFolios(r.folios)).catch(() => setFolios([]));
  }, [payment]);
  const visible = products.filter((p) => cat === 'all' || p.category === cat);
  const add = (p, d = 1) => setCart((c) => { const q = (c[p.id]?.qty || 0) + d; const n = { ...c }; if (q <= 0) delete n[p.id]; else n[p.id] = { p, qty: q }; return n; });
  const lines = Object.values(cart);
  const total = lines.reduce((s, l) => s + l.p.price * l.qty, 0);
  const sell = async () => {
    setBusy(true); setErr(''); setDone(null);
    try {
      const t = await apiCall('/api/admin/bar/tickets', { method: 'POST', body: JSON.stringify({ lines: lines.map((l) => ({ product_id: l.p.id, qty: l.qty })), payment, booking_id: payment === 'room' ? bookingId : null, spot: spot || null }) });
      setDone(t); setCart({}); onSold?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const chips = ['all', ...cats].map((c) => (
    <button key={c} type="button" onClick={() => setCat(c)} data-testid={`bar-cat-${c}`}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${cat === c ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>{c === 'all' ? 'All' : labels[c] || c}</button>
  ));
  const grid = visible.map((p) => (
    <button key={p.id} type="button" onClick={() => add(p)} data-testid={`bar-product-${p.id}`}
      className="text-left rounded-xl border bg-white p-3 hover:border-orange-400 hover:shadow-sm active:scale-[0.98] transition">
      <p className="text-sm font-semibold text-slate-900 leading-tight">{p.name}</p>
      <p className="text-xs text-slate-500 mt-0.5">{labels[p.category] || p.category}</p>
      <div className="flex items-center justify-between mt-2">
        <span className="font-bold text-slate-900">{money(p.price)}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.stock_units <= 0 ? 'bg-red-100 text-red-700' : p.low ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`} data-testid={`bar-product-stock-${p.id}`}>
          {p.stock_units} {p.unit}
        </span>
      </div>
    </button>
  ));
  const cartRows = lines.map((l) => (
    <li key={l.p.id} className="flex items-center gap-2 text-sm" data-testid={`bar-cart-${l.p.id}`}>
      <span className="flex-1 min-w-0 truncate text-slate-800">{l.p.name}</span>
      <button type="button" onClick={() => add(l.p, -1)} className="border rounded p-1 hover:bg-slate-50" data-testid={`bar-cart-minus-${l.p.id}`}><Minus className="w-3 h-3" /></button>
      <span className="w-6 text-center font-semibold" data-testid={`bar-cart-qty-${l.p.id}`}>{l.qty}</span>
      <button type="button" onClick={() => add(l.p, 1)} className="border rounded p-1 hover:bg-slate-50" data-testid={`bar-cart-plus-${l.p.id}`}><Plus className="w-3 h-3" /></button>
      <span className="w-16 text-right font-semibold">{money(l.p.price * l.qty)}</span>
    </li>
  ));
  const folioList = folios;
  const spotOpts = spots.map((x) => <option key={x.id} value={x.label}>{x.label}</option>);
  const folioOpts = folioList.map((f) => <option key={f.booking_id} value={f.booking_id}>{f.room} · {f.guest_name}</option>);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="bar-pos">
      <div className="lg:col-span-2 space-y-3">
        <div className="flex flex-wrap gap-2">{chips}</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">{grid}</div>
      </div>
      <div className="bg-white border rounded-xl p-4 space-y-3 h-fit lg:sticky lg:top-4" data-testid="bar-cart">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Ticket</h3>
          {lines.length > 0 && <button type="button" onClick={() => setCart({})} className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1" data-testid="bar-cart-clear"><Trash2 className="w-3 h-3" /> Clear</button>}
        </div>
        {lines.length === 0 ? <p className="text-sm text-slate-400">Tap products to add them.</p> : <ul className="space-y-2">{cartRows}</ul>}
        <div className="flex items-center justify-between border-t pt-3">
          <span className="text-sm text-slate-600">Total (VAT included)</span>
          <span className="text-xl font-bold text-slate-900" data-testid="bar-cart-total">{money(total)}</span>
        </div>
        <select value={spot} onChange={(e) => setSpot(e.target.value)} className="w-full border rounded-lg px-2 py-2 text-sm" data-testid="bar-spot-select">
          <option value="">Spot: bar counter / walk-in</option>{spotOpts}
        </select>
        <div className="flex gap-2">
          <PayButton id="cash" icon={Banknote} label="Cash" active={payment === 'cash'} onClick={() => setPayment('cash')} />
          <PayButton id="card" icon={CreditCard} label="Card" active={payment === 'card'} onClick={() => setPayment('card')} />
          <PayButton id="room" icon={BedDouble} label="Room" active={payment === 'room'} onClick={() => setPayment('room')} />
        </div>
        {payment === 'room' && (
          <select value={bookingId} onChange={(e) => setBookingId(e.target.value)} className="w-full border rounded-lg px-2 py-2 text-sm" data-testid="bar-room-select">
            <option value="">Choose checked-in guest…</option>{folioOpts}
          </select>
        )}
        <button type="button" onClick={sell} disabled={busy || lines.length === 0 || (payment === 'room' && !bookingId)} data-testid="bar-sell"
          className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold rounded-lg py-3">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {payment === 'room' ? 'Charge to room' : `Take ${payment}`} · {money(total)}
        </button>
        {err && <p className="text-xs text-red-600" data-testid="bar-sell-error">{err}</p>}
        {done && (
          <p className="text-xs text-emerald-700 inline-flex items-start gap-1" data-testid="bar-sell-done">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            <span>Ticket #{done.number} · {money(done.total)} · {done.payment === 'room' ? `charged to ${done.room} (${done.guest_name})` : `invoice ${done.invoice_number}${done.fiscal_status === 'fiscalized' ? ' · fiscalized' : done.fiscal_status === 'not_configured' ? ' · fiscalization not configured yet' : done.fiscal_status === 'failed' ? ' · EFI failed, retry in Finance' : ''}`}</span>
            <button type="button" onClick={() => printReceipt(done.id).catch((e) => setErr(e.message))} className="ml-2 inline-flex items-center gap-1 border border-emerald-300 rounded px-2 py-0.5 text-[11px] font-semibold hover:bg-emerald-100" data-testid="bar-print-receipt"><Printer className="w-3 h-3" /> Print receipt</button>
          </p>
        )}
      </div>
    </div>
  );
};

export default BarPos;
